import React, { FC, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { LegCreate, LegCreateProps } from "../Legs/LegCreate";
import { IClient } from "../../../types";
import { Button, Badge, ButtonDropdown } from "../../../shared";
import { renderAmount } from "../../../utils";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useConfig } from "../../../hooks/useConfig";
import { getProductTerminology } from "../../../constants/product-terminology";

interface LegsSectionProps {
  legCreateLayout: boolean;
  legsData: any[];
  form: any;
  client?: IClient;
  onFormUpdate?: (data: any) => void;
  onLegsUpdate?: (
    legs: LegsFormState[] | ((prevLegs: LegsFormState[]) => LegsFormState[])
  ) => void;
  initialData?: Record<string, any>;
}

interface LegsFormState {
  client?: IClient;
  appointmentDate: Date;
  form: any;
  legsData: any[];
  orderNumber: string;
  loadType: string;
  weight: number;
  driver: { fullName: string; _id: any };
  origin: any;
  destination: any;
  totalMiles: number;
  baseCharge: number;
  fuelSurcharge: number;
  dropCharge: number;
  otherCharges: number;
  totalCharges: number;
  useLegFlatRate: boolean;
  useCustomMileage: boolean;
  useCustomBaseCharge: boolean;
  useCustomFuelSurcharge: boolean;
  useCustomDropCharge: boolean;
  useCustomOtherCharges: boolean;
  waitTime: boolean;
  totalWaitTime: number;
  waitStart: any;
  waitEnd: any;
  hasHandoff: boolean;
  isHandoff: boolean;
  [key: string]: any; // Allow additional properties
}

const LegsSection: FC<LegsSectionProps> = ({
  legCreateLayout = false,
  client,
  legsData,
  form,
  onFormUpdate,
  onLegsUpdate,
  initialData = {},
}) => {
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  // Create initial form state
  const createInitialFormState = (): LegsFormState => ({
    form: {},
    client,
    legsData: [],
    appointmentDate: null,
    orderNumber: "",
    loadType: "",
    weight: 0,
    driver: { fullName: "", _id: null },
    origin: null,
    destination: null,
    route: null,
    totalMiles: 0,
    baseCharge: 0,
    fuelSurcharge: 0,
    dropCharge: 0,
    otherCharges: 0,
    totalCharges: 0,
    useLegFlatRate: false,
    useCustomMileage: false,
    useCustomBaseCharge: false,
    useCustomFuelSurcharge: false,
    useCustomDropCharge: false,
    useCustomOtherCharges: false,
    waitTime: false,
    totalWaitTime: 0,
    waitStart: null,
    waitEnd: null,
    hasHandoff: false,
    isHandoff: false,
    ...initialData,
  });
  // console.log("legCreateLayout", legCreateLayout);
  // Use legsData from props instead of internal state
  const legs =
    legsData && legsData.length > 0 ? legsData : [createInitialFormState()];
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Handle leg field updates. Uses a stable callback ref so that child
   * components (LegCreate / LegCreateForm) receive the same function
   * identity across renders, preventing unnecessary rerender cascades
   * that would unmount interactive widgets like MileageSelector.
   */
  const legsRefForUpdate = useRef(legs);
  legsRefForUpdate.current = legs;

  const updateLeg = useCallback(
    (legIndex: number, data: any) => {
      const currentLegs = legsRefForUpdate.current;
      const updatedLegs = [...currentLegs];
      if (data.id && data.value !== undefined) {
        // Handle single field update
        updatedLegs[legIndex] = {
          ...updatedLegs[legIndex],
          [data.id]: data.value,
        };
      } else {
        // Handle bulk update
        updatedLegs[legIndex] = { ...updatedLegs[legIndex], ...data };
      }

      onLegsUpdate?.(updatedLegs);
      onFormUpdate?.(data);
    },
    [onLegsUpdate, onFormUpdate]
  );

  // Handle mileage updates from distance calculation
  // Use a ref to track pending updates and batch them to prevent race conditions
  const pendingUpdatesRef = useRef<Map<number, { miles: number; data: any }>>(
    new Map()
  );
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const legsRef = useRef(legs);

  useEffect(() => {
    legsRef.current = legs;

    // When new legs data arrives (e.g. from extraction), discard any
    // pending mileage updates that were queued against the OLD state.
    // Without this, a stale update queued during the initial empty-leg
    // render can fire 50ms later and overwrite correct extraction data
    // with totalMiles: 0.
    if (pendingUpdatesRef.current.size > 0) {
      pendingUpdatesRef.current.clear();
    }
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }
  }, [legs]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Process all pending updates in a batch
  const processPendingUpdates = useCallback(() => {
    if (pendingUpdatesRef.current.size === 0 || !onLegsUpdate) return;

    // Get the latest legs state
    const currentLegs = legsRef.current || legs;
    const updatedLegs = [...currentLegs];

    // Apply all pending updates
    pendingUpdatesRef.current.forEach((update, legIndex) => {
      // When useCustomMileage is true, preserve existing defaultMileage (route value)
      // and only update totalMiles with the billed value
      const preserveDefaultMileage = update.data?.useCustomMileage === true;

      // Guard: never let a stale update.miles of 0 overwrite valid mileage
      // that already exists on the leg (e.g. from extraction data).
      // This prevents the initial-mount loadType effect (which fires with
      // totalMiles=0 before extraction arrives) from zeroing out correct values.
      const existingMiles = updatedLegs[legIndex]?.totalMiles || 0;
      const safeMiles =
        update.miles > 0 ? update.miles : existingMiles || update.miles;

      updatedLegs[legIndex] = {
        ...updatedLegs[legIndex],
        totalMiles: safeMiles,
        defaultMileage: preserveDefaultMileage
          ? update.data.defaultMileage || updatedLegs[legIndex].defaultMileage || safeMiles
          : update.data.defaultMileage || update.data.totalMiles || safeMiles,
        baseCharge:
          update.data.baseCharge !== undefined
            ? update.data.baseCharge
            : updatedLegs[legIndex].baseCharge,
        fuelSurcharge:
          update.data.fuelSurcharge !== undefined
            ? update.data.fuelSurcharge
            : updatedLegs[legIndex].fuelSurcharge,
        fullRouteData:
          update.data.fullRouteData || updatedLegs[legIndex].fullRouteData,
        // Spread any other fields from the data object
        ...update.data,
      };
    });

    // Clear pending updates
    pendingUpdatesRef.current.clear();

    // Update the ref
    legsRef.current = updatedLegs;

    // Notify parent of the batched update
    onLegsUpdate(updatedLegs);
  }, [legs, onLegsUpdate]);

  const handleMileageUpdate = useCallback(
    (legIndex: number) => {
      return (miles: number, data: any) => {
        // Add this update to the pending queue
        pendingUpdatesRef.current.set(legIndex, { miles, data });

        // Clear any existing timeout
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        // Batch updates: wait a short time for other updates to arrive, then process all at once
        updateTimeoutRef.current = setTimeout(() => {
          processPendingUpdates();
          updateTimeoutRef.current = null;
        }, 50); // 50ms batching window
      };
    },
    [processPendingUpdates]
  );

  /**
   * Initialize leg form with data and options.
   * Stable callback – reads current legs from a ref so the identity
   * stays the same across renders and avoids child unmount/remount.
   */
  const setLegFormInit = useCallback(
    (
      legIndex: number,
      initData: Record<string, any>,
      options?: { setErrors?: boolean; reset?: boolean }
    ) => {
      const currentLegs = legsRefForUpdate.current;
      const updatedLegs = [...currentLegs];
      if (options?.reset) {
        const freshDefaults = createInitialFormState();
        const merged = { ...freshDefaults, ...initData };

        // Guard: if initData would zero-out totalMiles but the current leg
        // already has valid mileage (from extraction), preserve it.
        const currentMiles = updatedLegs[legIndex]?.totalMiles || 0;
        if (currentMiles > 0 && !merged.totalMiles) {
          merged.totalMiles = currentMiles;
        }
        // Same for defaultMileage
        const currentDefault = updatedLegs[legIndex]?.defaultMileage || 0;
        if (currentDefault > 0 && !merged.defaultMileage) {
          merged.defaultMileage = currentDefault;
        }
        // Preserve route if the update doesn't provide one
        if (updatedLegs[legIndex]?.route && !initData.route) {
          merged.route = updatedLegs[legIndex].route;
        }

        updatedLegs[legIndex] = merged;
      } else {
        updatedLegs[legIndex] = { ...updatedLegs[legIndex], ...initData };
      }

      onLegsUpdate?.(updatedLegs);

      if (options?.setErrors) {
        setErrors({});
      }
    },
    [createInitialFormState, onLegsUpdate]
  );

  // Add a new leg
  const addNewLeg = () => {
    const newLeg = createInitialFormState();

    // Auto-populate origin from previous leg's destination
    if (legs.length > 0) {
      const previousLeg = legs[legs.length - 1];
      if (previousLeg.destination) {
        newLeg.origin = previousLeg.destination;
      }
    }

    const updatedLegs = [...legs, newLeg];
    onLegsUpdate?.(updatedLegs);
  };

  // Remove a leg
  const removeLeg = (legIndex: number) => {
    if (legs.length > 1) {
      const updatedLegs = legs.filter((_, index) => index !== legIndex);
      onLegsUpdate?.(updatedLegs);
    }
  };

  // Utility function to get nested object values (like Object.byString)
  const getNestedValue = (obj: any, path: string): any => {
    if (!path) return obj;
    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  };

  // Render value function - gets nested values from leg form
  const renderLegValue = (legIndex: number) => (valueId: string) => {
    const value = getNestedValue(legs[legIndex], valueId);
    return value;
  };

  // Render ID function for form field IDs
  const renderId = (id: string) => id;

  // Calculate total charges for all legs using the most reliable fields
  const renderLegTotalCharges = (leg: LegsFormState) => {
    if (leg.calculatedTotalCharges != null) return leg.calculatedTotalCharges;
    if (leg.totalCharges != null) return leg.totalCharges;
    return (
      (leg.baseCharge || 0) +
      (leg.fuelSurcharge || 0) +
      (leg.dropCharge || 0) +
      (leg.otherCharges || 0)
    );
  };

  function handleAddReturn() {
    const origin =
      legs && legs.length > 0 ? legs[legs.length - 1]?.destination : null;
    const destination = legs && legs.length > 0 ? legs[0]?.origin : null;
    const newLeg = createInitialFormState();
    newLeg.client = client;
    newLeg.job = form;
    newLeg.legsData = legs;
    newLeg.useLegFlatRate = false;
    newLeg.origin = origin;
    newLeg.destination = destination; // set destination to first leg's origin
    newLeg.orderNumber = null;
    newLeg.loadNumber = null;
    newLeg.loadType = "Empty";
    newLeg.route = {
      origin,
      destination,
    };
    newLeg.weight = 0;
    newLeg.driver = { fullName: "", _id: null };
    newLeg.appointmentTime = null;
    newLeg.appointmentDate = null;
    newLeg.waitEnd = null;
    newLeg.waitStart = null;
    newLeg.waitTime = false;
    newLeg.totalWaitTime = 0;
    newLeg.baseCharge = 0;
    newLeg.fuelSurcharge = 0;
    newLeg.otherCharges = 0;
    newLeg.dropCharge = 0;
    newLeg.useCustomFuelSurcharge = false;
    newLeg.useCustomBaseCharge = false;
    newLeg.useCustomDropCharge = false;
    newLeg.useCustomOtherCharges = false;
    newLeg.totalMiles = 0;
    newLeg.totalCharges = 0;
    newLeg.hasHandoff = null;
    newLeg.isHandoff = false;

    const updatedLegs = [...legs, newLeg];
    onLegsUpdate?.(updatedLegs);
  }

  /**
   * Pre-build stable mileage-update handlers for each leg index.
   * This prevents creating a new function reference on every render
   * (handleMileageUpdate(index) returns a new closure each call),
   * which would otherwise cause LegCreateForm to re-render and
   * unmount/remount the MileageSelector mid-interaction.
   */
  const mileageUpdateHandlers = useMemo(() => {
    return legs.map((_, index) => handleMileageUpdate(index));
  }, [legs.length, handleMileageUpdate]);

  const addLegDropdownData = [
    {
      title: "Add Return",
      onClick: handleAddReturn,
      disabled: !(legs && legs.length > 0),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border px-4 py-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-md font-semibold text-gray-900">
            {terms.legsSectionTitle}
          </h3>
          <div className="flex items-center gap-4">
            <ButtonDropdown
              split
              type="primary"
              title="Add Leg"
              size="xs"
              icon={<PlusCircleIcon className="h-5 w-5 mr-1" />}
              onClick={addNewLeg}
              data={addLegDropdownData}
            ></ButtonDropdown>
          </div>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          {legs.map((leg, index) => {
            const legCreateProps: Omit<
              LegCreateProps,
              "form" | "updateForm" | "setFormInit"
            > = {
              errors,
              client,
              jobCreate: true,
              jobCreateLayout: true,
              title: `Leg ${index + 1}`,
              size: "lg",
              renderValue: renderLegValue(index),
              renderId,
            };

            return (
              <div key={index} className="border rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <Badge
                    label={`Leg ${index + 1}`}
                    className="bg-primary-100 text-primary-800 border border-primary-200"
                  />
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-neutral-700">
                      {renderAmount(renderLegTotalCharges(leg))}
                    </div>
                    {legs.length > 1 && (
                      <Button
                        type="light"
                        title="Remove"
                        size="xs"
                        onClick={() => removeLeg(index)}
                      >
                        {/* Remove */}
                      </Button>
                    )}
                  </div>
                </div>

                <LegCreate
                  {...legCreateProps}
                  // jobCreate={true}
                  jobCreateLayout={legCreateLayout}
                  form={leg}
                  size="sm"
                  updateForm={(data) => updateLeg(index, data)}
                  setFormInit={(initData, options) =>
                    setLegFormInit(index, initData, options)
                  }
                  onMileageUpdate={mileageUpdateHandlers[index]}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LegsSection;
